import {
	ArgumentScale,
	EventTracker,
	HoverState,
	LineSeries,
	NotifyPointerMoveFn,
	RawAxisProps,
	SeriesProps,
	SeriesRef,
	Stack,
	ValueScale,
} from '@devexpress/dx-react-chart';
import {
	Chart,
	Tooltip,
	ArgumentAxis,
	ValueAxis,
	BarSeries,
	Title,
	Legend,
	SplineSeries,
	ZoomAndPan,
} from '@devexpress/dx-react-chart-material-ui';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { format } from 'd3-format';
import { Colors } from 'shared/assets/root-assets/colors';
import { SkioldTouchableOpacity } from '../../skiold-components/skiold-touchable-opacity';
import { ViewWeb } from '../../utils/web-view';
import { WhiteText } from '../../Text/white-text';
import '../graph.scss';

interface Props {
	barSeries: SeriesProps[];
	title: string;
	chartData: any[];
	stack?: boolean;
	valueAxis?: RawAxisProps[];
	trendLine?: SeriesProps;
	keyTranslator?: (key: string) => string | undefined;
	yTitle?: string;
	zoomPan?: 'none' | 'pan' | 'zoom' | 'both';
	verticalArgumentLabels?: boolean;
}

const makeYLabel = (symbol, color) => (props: ValueAxis.LabelProps) =>
	(
		<ValueAxis.Label
			{...props}
			text={`${props.text} ${symbol}`}
			style={{
				fill: color,
			}}
		/>
	);

const makeXLabel = (symbol, color, vertialLabels) => (props: ValueAxis.LabelProps) =>
	vertialLabels ? (
		<ArgumentAxis.Label
			{...props}
			x={-25 - props.y}
			y={+props.x -8}
			text={`${props.text} ${symbol}`}
			style={{
				fill: color,
				transform: 'rotate(270deg) translateX(-10px)',
			}}
		/>
	) : (
		<ArgumentAxis.Label
			{...props}
			text={`${props.text} ${symbol}`}
			style={{
				fill: color,
			}}
		/>
	);

const TitleText = props => <Title.Text {...props} style={{ color: Colors.white }} />;

export const BarChart: FC<Props> = React.memo(
	({
		yTitle,
		barSeries,
		title,
		zoomPan = 'none',
		chartData,
		stack,
		valueAxis,
		trendLine,
		verticalArgumentLabels,
		keyTranslator = (key: string) => key,
	}) => {
		// States
		const [target, setTarget] = useState<SeriesRef | undefined>(undefined);
		const [hideLegend, setHideLegend] = useState<string[]>([]);
		const yAxis = useMemo(() => {
			return valueAxis ? (
				valueAxis.map((props: RawAxisProps) => {
					const label = makeYLabel('', Colors.white);
					return <ValueAxis {...props} labelComponent={label} key={props.scaleName} showLine={true} />;
				})
			) : (
				<ValueAxis />
			);
		}, [valueAxis]);

		// Legend for Hide/Show toggling
		const LegendLabel = useCallback(
			props => (
				<SkioldTouchableOpacity
					onPress={() => {
						// If the legend is already hidden, remove it from the list
						if (hideLegend.includes(props.text)) {
							setHideLegend(hideLegend.filter(x => x !== props.text));
						} else {
							// Otherwise, add it to the list
							setHideLegend([...hideLegend, props.text]);
						}
					}}
				>
					<Legend.Label
						{...props}
						text={keyTranslator(props.text)}
						style={{ color: Colors.white }}
						className={hideLegend.includes(props.text) ? 'line-through' : ''}
					/>
				</SkioldTouchableOpacity>
			),
			[hideLegend],
		);

		const RootWithTitle = useCallback(
			props => (
				<ViewWeb className="graph-legend-root">
					<div className="rotate-container">
						<WhiteText className="header-white-text rotate-270">{yTitle}</WhiteText>
					</div>

					<Legend.Root {...props} className='graph-legend-root-list' />
				</ViewWeb>
			),
			[],
		);

		// Legend for Hide/Show toggling
		const LegendMarker = useCallback(
			props => (
				<SkioldTouchableOpacity
					onPress={() => {
						// If the legend is already hidden, remove it from the list
						if (hideLegend.includes(props.name)) {
							setHideLegend(hideLegend.filter(x => x !== props.name));
						} else {
							// Otherwise, add it to the list
							setHideLegend([...hideLegend, props.name]);
						}
					}}
				>
					<Legend.Marker {...props} className={hideLegend.includes(props.name) ? 'line-through' : ''} />
				</SkioldTouchableOpacity>
			),
			[hideLegend],
		);
		// Series
		const series = useMemo(() => {
			return barSeries.map((props: SeriesProps) => {
				return <BarSeries {...props} key={props.valueField} />;
			});
		}, [barSeries]);

		// Stacks
		const stacks = useMemo(() => {
			return stack ? [{ series: barSeries.map((props: SeriesProps) => props.name) } as Stack] : [];
		}, [barSeries, stack]);

		// Trendline
		const trend = useMemo(() => {
			return trendLine ? <SplineSeries {...trendLine} /> : null;
		}, [trendLine]);

		// Hover
		const onHover = useCallback((target: SeriesRef | null) => {
			setTarget(target ?? undefined);
		}, []);

		// Hide legend values
		const setHideLegendValuesToZero = (singleChartData: any) => {
			const keys = Object.keys(singleChartData);
			// Set values to zero if the legend is hidden
			// We can't just remove BarSeries, legend will then disappear
			keys.forEach(key => {
				if (hideLegend.includes(key)) {
					singleChartData[key] = 0;
				}
			});
			return singleChartData;
		};

		// Filter hiden legend data
		const filterHidenLegendData = useMemo(() => {
			return chartData.slice().map(x => setHideLegendValuesToZero({ ...x }));
		}, [chartData, hideLegend]);

		const tooltip = useCallback(
			(target: Tooltip.ContentProps) => {
				const items = barSeries
					.slice()
					.reverse()
					.map(bar => {
						// If the bar has no name, it should not be displayed
						if (bar.name === undefined || hideLegend.includes(bar.name)) {
							return null;
						}
						// Find the value for the hovered point
						const val = filterHidenLegendData[target.targetItem.point][bar.valueField];
						if (!val) {
							return null;
						}
						const mouseoverTarget = bar.valueField === target.targetItem.series;
						return (
							<tr
								className={mouseoverTarget ? 'bold-label-text' : ''}
								key={target.targetItem.series + '-' + target.targetItem.point + '-' + bar.valueField}
							>
								<td>
									<svg width="10" height="10">
										<circle cx="5" cy="5" r="5" fill={bar.color} />
									</svg>
								</td>
								<td>{keyTranslator(bar.name)}</td>
								<td align="right">{val}</td>
							</tr>
						);
					});
				return <table>{items}</table>;
			},
			[barSeries, filterHidenLegendData, hideLegend],
		);
		return (
			<Chart data={filterHidenLegendData}>
				<ArgumentAxis labelComponent={makeXLabel('', Colors.white, verticalArgumentLabels)} />
				{yAxis}
				{series}
				{trend}
				<Title textComponent={TitleText} text={title} />
				<Legend labelComponent={LegendLabel} markerComponent={LegendMarker} rootComponent={RootWithTitle} />
				<EventTracker />
				<HoverState hover={target} onHoverChange={onHover} />
				<Tooltip targetItem={target} contentComponent={tooltip} />
				<Stack stacks={stacks} />
				{zoomPan !== 'none' && <ZoomAndPan interactionWithArguments={zoomPan} />}
			</Chart>
		);
	},
);
